import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { bookingsAPI, roomsAPI, teamsAPI } from "../../services/meetingsAPI";
import Navbar from "../../components/Navbar";
import "./StatusPage.css";

const RoomMemberStatus = () => {
  const navigate = useNavigate();
  const [rooms, setRooms] = useState([]);
  const [allMembers, setAllMembers] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [currentMeetings, setCurrentMeetings] = useState([]);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAllMembers = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      let teams = [];
      if (token) {
        try {
          const res = await teamsAPI.getMyTeams();
          teams = res.data || [];
        } catch {
          const res = await teamsAPI.getAllTeams();
          teams = res.data || [];
        }
      } else {
        const res = await teamsAPI.getAllTeams();
        teams = res.data || [];
      }
      const membersMap = new Map();
      teams.forEach((team) => {
        if (team.teamLeader) {
          membersMap.set(team.teamLeader.email, {
            name: team.teamLeader.name,
            email: team.teamLeader.email,
            team: team.teamName,
            isLeader: true,
          });
        }
        team.members?.forEach((member) => {
          if (!membersMap.has(member.email)) {
            membersMap.set(member.email, {
              name: member.name,
              email: member.email,
              team: team.teamName,
              isLeader: false,
            });
          }
        });
      });
      return Array.from(membersMap.values());
    } catch (err) {
      console.error("Error fetching members:", err);
      return [];
    }
  }, []);

  const fetchRooms = useCallback(async () => {
    try {
      const res = await roomsAPI.getRooms();
      const apiRooms = res.data || [];
      const localRooms = JSON.parse(localStorage.getItem("meetingRooms") || "[]");
      const byId = new Map(apiRooms.map((r) => [r._id, { ...r, name: r.name }]));
      localRooms.forEach((r) => {
        const id = r._id || r.id;
        if (id && !byId.has(id)) {
          byId.set(id, { _id: id, name: r.roomName || r.name || "Meeting Room" });
        }
      });
      return Array.from(byId.values());
    } catch (err) {
      console.error("Error fetching rooms:", err);
      return [];
    }
  }, []);

  const fetchBookings = useCallback(async () => {
    try {
      const today = new Date().toISOString().split("T")[0];
      const res = await bookingsAPI.getBookingsByDate(today, true);
      return res.data || [];
    } catch (err) {
      console.error("Error fetching bookings:", err);
      return [];
    }
  }, []);

  const getCurrentMeetings = useCallback(() => {
    const now = new Date();
    return bookings.filter((booking) => {
      const start = new Date(booking.startTime);
      const end = new Date(booking.endTime);
      return start <= now && now < end && booking.status === "confirmed";
    });
  }, [bookings]);

  const getMemberCurrentMeeting = useCallback(
    (memberEmail) => {
      const now = new Date();
      return bookings.find((booking) => {
        const start = new Date(booking.startTime);
        const end = new Date(booking.endTime);
        const isOngoing =
          start <= now && now < end && booking.status === "confirmed";
        if (!isOngoing) return false;
        return (
          booking.selectedMembers?.some((m) => m.email === memberEmail) ||
          booking.teamInfo?.teamLeader?.email === memberEmail
        );
      });
    },
    [bookings]
  );

  const loadData = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      const [bookingsData, roomsData, membersData] = await Promise.all([
        fetchBookings(),
        fetchRooms(),
        fetchAllMembers(),
      ]);
      setBookings(bookingsData);
      setRooms(roomsData);
      setAllMembers(membersData);
      setLastUpdate(new Date());
    } catch (err) {
      console.error("Error loading dashboard data:", err);
      setError("Failed to load dashboard. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [fetchBookings, fetchRooms, fetchAllMembers]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    const interval = setInterval(loadData, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [loadData]);

  useEffect(() => {
    setCurrentMeetings(getCurrentMeetings());
  }, [bookings, getCurrentMeetings]);

  const availableRooms = Math.max(rooms.length - currentMeetings.length, 0);
  const busyMembersCount = allMembers.filter((member) =>
    Boolean(getMemberCurrentMeeting(member.email))
  ).length;
  const freeMembersCount = Math.max(allMembers.length - busyMembersCount, 0);
  const todayLabel = new Date().toLocaleDateString("en-IN", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
  const lastUpdatedLabel = lastUpdate.toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 page-content">
        <Navbar isScrolled={false} onSignInClick={() => {}} showLoginModal={false} />
        <div className="status-dashboard status-dashboard--loading">
          <div className="status-dashboard__spinner" />
          <p className="status-dashboard__loading-text">Loading dashboard…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 page-content">
        <Navbar isScrolled={false} onSignInClick={() => {}} showLoginModal={false} />
        <div className="status-dashboard status-dashboard--error">
          <p className="status-dashboard__error-text">{error}</p>
          <button type="button" className="status-dashboard__retry" onClick={loadData}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 page-content">
      <Navbar isScrolled={false} onSignInClick={() => {}} showLoginModal={false} />
      <div className="status-dashboard">
        <section className="status-dashboard__hero">
          <div className="status-dashboard__hero-top">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="status-dashboard__back-btn"
              aria-label="Go back"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back
            </button>

            <div className="status-dashboard__meta">
              <span className="status-dashboard__updated">Today: {todayLabel}</span>
              <button
                type="button"
                className="status-dashboard__refresh"
                onClick={loadData}
                aria-label="Refresh dashboard"
              >
                Refresh
              </button>
            </div>
          </div>

          <header className="status-dashboard__header">
            <div className="status-dashboard__header-copy">
              <p className="status-dashboard__eyebrow">Live Workspace</p>
              <h1 className="status-dashboard__title">Status</h1>
              <p className="status-dashboard__subtitle">
                Track room occupancy and member availability in one polished dashboard without leaving the workspace.
              </p>
            </div>

            <div className="status-dashboard__hero-stats">
              <div className="status-dashboard__stat-card">
                <span className="status-dashboard__stat-label">Available Rooms</span>
                <strong className="status-dashboard__stat-value">{availableRooms}</strong>
                <span className="status-dashboard__stat-note">{rooms.length} total rooms</span>
              </div>
              <div className="status-dashboard__stat-card">
                <span className="status-dashboard__stat-label">Ongoing Meetings</span>
                <strong className="status-dashboard__stat-value">{currentMeetings.length}</strong>
                <span className="status-dashboard__stat-note">Updated at {lastUpdatedLabel}</span>
              </div>
              <div className="status-dashboard__stat-card">
                <span className="status-dashboard__stat-label">Members Free</span>
                <strong className="status-dashboard__stat-value">{freeMembersCount}</strong>
                <span className="status-dashboard__stat-note">{busyMembersCount} in meetings</span>
              </div>
            </div>
          </header>
        </section>

        <div className="status-dashboard__grid">
          {/* Room Availability */}
          <section className="status-dashboard__card status-dashboard__card--rooms">
            <div className="status-dashboard__card-header">
              <h2 className="status-dashboard__card-title">Room availability</h2>
              <span className="status-dashboard__card-badge">{rooms.length} rooms</span>
            </div>
            <div className="status-dashboard__card-body status-dashboard__card-body--scroll">
              {rooms.length === 0 ? (
                <p className="status-dashboard__empty">No rooms configured.</p>
              ) : (
                <table className="status-dashboard__table">
                  <thead>
                    <tr>
                      <th>Room</th>
                      <th>Status</th>
                      <th>Current meeting</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rooms.map((room, index) => {
                      const currentMeeting = currentMeetings.find(
                        (m) => m.room?._id === room._id
                      );
                      const isAvailable = !currentMeeting;
                      return (
                        <tr
                          key={room._id}
                          className={index % 2 === 0 ? "" : "status-dashboard__row--alt"}
                        >
                          <td>
                            <div className="status-dashboard__entity">
                              <span
                                className={`status-dashboard__avatar ${
                                  isAvailable
                                    ? "status-dashboard__avatar--available"
                                    : "status-dashboard__avatar--busy"
                                }`}
                              >
                                {(room.name || "R").charAt(0).toUpperCase()}
                              </span>
                              <div>
                                <div className="status-dashboard__entity-name">{room.name}</div>
                                <div className="status-dashboard__entity-subtext">
                                  {isAvailable ? "Ready to book" : "Currently occupied"}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td>
                            <span
                              className={
                                isAvailable
                                  ? "status-dashboard__status status-dashboard__status--available"
                                  : "status-dashboard__status status-dashboard__status--busy"
                              }
                            >
                              {isAvailable ? "Available" : "In use"}
                            </span>
                          </td>
                          <td>
                            {isAvailable ? (
                              "—"
                            ) : (
                              <div className="status-dashboard__meeting-info">
                                <div className="status-dashboard__meeting-team">
                                  {currentMeeting.teamInfo?.name || currentMeeting.team}
                                </div>
                                <div className="status-dashboard__meeting-time">
                                  {new Date(currentMeeting.startTime).toLocaleTimeString(
                                    [],
                                    { hour: "2-digit", minute: "2-digit" }
                                  )}{" "}
                                  –{" "}
                                  {new Date(currentMeeting.endTime).toLocaleTimeString(
                                    [],
                                    { hour: "2-digit", minute: "2-digit" }
                                  )}
                                </div>
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </section>

          {/* Member Availability */}
          <section className="status-dashboard__card status-dashboard__card--members">
            <div className="status-dashboard__card-header">
              <h2 className="status-dashboard__card-title">Member availability</h2>
              <span className="status-dashboard__card-badge">{allMembers.length} members</span>
            </div>
            <div className="status-dashboard__card-body status-dashboard__card-body--scroll">
              {allMembers.length === 0 ? (
                <p className="status-dashboard__empty">No team members found.</p>
              ) : (
                <table className="status-dashboard__table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Status</th>
                      <th>Current meeting</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...allMembers]
                      .sort((a, b) => {
                        const aBusy = !!getMemberCurrentMeeting(a.email);
                        const bBusy = !!getMemberCurrentMeeting(b.email);
                        if (aBusy && !bBusy) return -1;
                        if (!aBusy && bBusy) return 1;
                        return 0;
                      })
                      .map((member, index) => {
                        const currentMeeting = getMemberCurrentMeeting(member.email);
                        const isBusy = !!currentMeeting;
                        return (
                          <tr
                            key={member.email}
                            className={
                              index % 2 === 0 ? "" : "status-dashboard__row--alt"
                            }
                          >
                            <td>
                              <div className="status-dashboard__entity">
                                <span
                                  className={`status-dashboard__avatar ${
                                    isBusy
                                      ? "status-dashboard__avatar--busy"
                                      : "status-dashboard__avatar--available"
                                  }`}
                                >
                                  {(member.name || "M").charAt(0).toUpperCase()}
                                </span>
                                <div>
                                  <div className="status-dashboard__entity-name">{member.name}</div>
                                  <div className="status-dashboard__entity-subtext">
                                    {member.team || (member.isLeader ? "Team leader" : "Member")}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td>
                              <span
                                className={
                                  isBusy
                                    ? "status-dashboard__status status-dashboard__status--busy"
                                    : "status-dashboard__status status-dashboard__status--available"
                                }
                              >
                                {isBusy ? "In meeting" : "Free"}
                              </span>
                            </td>
                            <td>
                              {isBusy ? (
                                <div className="status-dashboard__meeting-info">
                                  <div className="status-dashboard__meeting-room">
                                    {currentMeeting.room?.name}
                                  </div>
                                  <div className="status-dashboard__meeting-team">
                                    {currentMeeting.teamInfo?.name ||
                                      currentMeeting.team}
                                  </div>
                                  <div className="status-dashboard__meeting-time">
                                    {new Date(
                                      currentMeeting.startTime
                                    ).toLocaleTimeString([], {
                                      hour: "2-digit",
                                      minute: "2-digit",
                                    })}{" "}
                                    –{" "}
                                    {new Date(
                                      currentMeeting.endTime
                                    ).toLocaleTimeString([], {
                                      hour: "2-digit",
                                      minute: "2-digit",
                                    })}
                                  </div>
                                </div>
                              ) : (
                                "—"
                              )}
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              )}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default RoomMemberStatus;
